import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useTicketEvents } from '../../hooks/useTicketEvents';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { useDashboardTracking } from '../../hooks/useDashboardTracking';
import DateTimePickerInput from '../../components/DateTimePickerInput';

// Chart components
import ProblemStateBarChart from './components/charts/ProblemStateBarChart';
import PriorityDonutChart from './components/charts/PriorityDonutChart';
import ProblemBucketsChart from './components/charts/ProblemBucketsChart';
import ProblemTasksByGroupChart from './components/charts/ProblemTasksByGroupChart';
import ProblemTrendChart from './components/charts/ProblemTrendChart';
import UnifiedPulseReportTable from './components/UnifiedPulseReportTable';

// Utils
import { DurationPreset, durationPresets, convertPresetToTimeRange, getDurationPresetRange } from './utils/dateHelpers';

interface Customer {
  id: string;
  accountName: string;
}

interface Group {
  id: string;
  name: string;
}

interface TrendDataPoint {
  date: string;
  new: number;
  assessment: number;
  rca: number;
  resolved: number;
  closed: number;
  cancelled: number;
}

interface ChartFilter {
  visible: boolean;
  type: 'problemState' | 'priority' | 'bucket' | 'date' | 'assignmentGroup' | 'trend' | null;
  value: string | null;
  label: string;
  trendData?: TrendDataPoint[];
}

const ProblemDashboard: React.FC = () => {
  // Subscribe to real-time problem updates via SSE
  useTicketEvents({ ticketTypes: ['PROBLEM'] });

  // Get date formatter hook
  const { formatTime } = useDateFormatter();

  // Filter state
  const [customerId, setCustomerId] = useState<string>('');
  const [durationPreset, setDurationPreset] = useState<DurationPreset>('this_month');
  const [previousPreset, setPreviousPreset] = useState<DurationPreset>('this_month');
  const [customDateFrom, setCustomDateFrom] = useState<string>('');
  const [customDateTo, setCustomDateTo] = useState<string>('');
  const [appliedDateFrom, setAppliedDateFrom] = useState<string>('');
  const [appliedDateTo, setAppliedDateTo] = useState<string>('');
  const [assignmentGroupIds, setAssignmentGroupIds] = useState<string[]>([]);

  // Chart interaction state
  const [chartFilter, setChartFilter] = useState<ChartFilter>({
    visible: false,
    type: null,
    value: null,
    label: '',
  });

  // Handle duration preset change
  const handleDurationChange = (newPreset: DurationPreset) => {
    if (newPreset === 'custom') {
      // Pre-populate custom dates based on the previous preset's range
      const range = getDurationPresetRange(previousPreset);
      setCustomDateFrom(range.from);
      setCustomDateTo(range.to);
      // Don't set applied dates yet - wait for Apply button
    } else {
      // For non-custom presets, update the previous preset tracker
      setPreviousPreset(newPreset);
      // Clear custom date state
      setCustomDateFrom('');
      setCustomDateTo('');
      setAppliedDateFrom('');
      setAppliedDateTo('');
    }
    setDurationPreset(newPreset);
  };

  // Compute time range for API - uses applied dates for custom range
  const timeRange = useMemo(() => {
    if (durationPreset === 'custom') {
      // Only return custom range if dates have been applied
      if (appliedDateFrom && appliedDateTo) {
        return convertPresetToTimeRange('custom', appliedDateFrom, appliedDateTo);
      }
      // Keep using the previous preset's range until Apply is clicked
      return convertPresetToTimeRange(previousPreset);
    }
    return convertPresetToTimeRange(durationPreset);
  }, [durationPreset, appliedDateFrom, appliedDateTo, previousPreset]);

  // Handle applying custom date range
  const handleApplyCustomDates = () => {
    if (customDateFrom && customDateTo) {
      setAppliedDateFrom(customDateFrom);
      setAppliedDateTo(customDateTo);
    }
  };

  // Check if custom dates have been modified and need to be applied
  const customDatesNeedApply = durationPreset === 'custom' &&
    customDateFrom && customDateTo &&
    (customDateFrom !== appliedDateFrom || customDateTo !== appliedDateTo);

  // Track dashboard filters for Application Insights
  useDashboardTracking('Problem', {
    customerId,
    durationPreset,
    assignmentGroupIds,
    customDateFrom: appliedDateFrom || undefined,
    customDateTo: appliedDateTo || undefined,
  });

  // Fetch customers
  const { data: customers = [] } = useQuery<Customer[]>({
    queryKey: ['customers-list'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data.customers || response.data || [];
    },
  });

  // Fetch all assignment groups for filtering
  const { data: groups = [] } = useQuery<Group[]>({
    queryKey: ['all-assignment-groups'],
    queryFn: async () => {
      const response = await api.get('/groups?type=assignment&limit=500');
      return response.data.groups || response.data || [];
    },
  });

  // Get user's timezone offset in minutes
  const timezoneOffset = new Date().getTimezoneOffset();

  // Fetch problem dashboard data with fresh data on every mount/tab focus
  const { data: dashboardData, isLoading, error, refetch, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['problem-dashboard', customerId, timeRange, assignmentGroupIds, timezoneOffset],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (customerId) params.append('customerId', customerId);
      if (timeRange) params.append('timeRange', timeRange);
      if (assignmentGroupIds.length > 0) params.append('assignmentGroupId', assignmentGroupIds.join(','));
      params.append('timezoneOffset', String(timezoneOffset));

      const response = await api.get(`/dashboard/problem?${params}`);
      return response.data;
    },
    staleTime: 0,               // Always fetch fresh data on mount
    refetchOnWindowFocus: true, // Refresh when user returns to tab
    refetchOnMount: 'always',   // Always refetch on component mount
  });

  // Handle chart clicks
  const handleStateClick = (state: string) => {
    const stateLabels: Record<string, string> = {
      new: 'New',
      assess_1: 'Assess 1',
      assess_2: 'Assess 2',
      root_cause_analysis: 'Root Cause Analysis',
      resolved: 'Resolved',
      closed: 'Closed',
      cancelled: 'Cancelled',
    };
    setChartFilter({
      visible: true,
      type: 'problemState',
      value: state,
      label: `State: ${stateLabels[state] || state}`,
    });
  };

  const handlePriorityClick = (priority: string) => {
    const priorityLabels: Record<string, string> = {
      critical_1: 'Critical',
      high_2: 'High',
      moderate_3: 'Moderate',
      low_4: 'Low',
      planning_5: 'Planning',
    };
    setChartFilter({
      visible: true,
      type: 'priority',
      value: priority,
      label: `Priority: ${priorityLabels[priority] || priority}`,
    });
  };

  const handleBucketClick = (bucket: string) => {
    const bucketLabels: Record<string, string> = {
      assessment: 'Assessment',
      rca: 'Root Cause Analysis',
      resolved: 'Resolved',
    };
    setChartFilter({
      visible: true,
      type: 'bucket',
      value: bucket,
      label: `Bucket: ${bucketLabels[bucket] || bucket}`,
    });
  };

  const handleGroupClick = (groupId: string, groupName: string) => {
    setChartFilter({
      visible: true,
      type: 'assignmentGroup',
      value: groupId,
      label: `Assignment Group: ${groupName}`,
    });
  };

  // Tile click handlers
  const handleTotalProblemsClick = () => {
    setChartFilter({
      visible: true,
      type: 'problemState',
      value: 'all',
      label: 'Total Problems',
    });
  };

  const handleOpenProblemsClick = () => {
    setChartFilter({
      visible: true,
      type: 'bucket',
      value: 'open',
      label: 'Open Problems',
    });
  };

  const handleResolvedClick = () => {
    setChartFilter({
      visible: true,
      type: 'problemState',
      value: 'resolved',
      label: 'Resolved Problems',
    });
  };

  const handleClosedClick = () => {
    setChartFilter({
      visible: true,
      type: 'problemState',
      value: 'closed',
      label: 'Closed Problems',
    });
  };

  const handleTrendPointClick = (_date: string) => {
    // Show trend summary table
    setChartFilter({
      visible: true,
      type: 'trend',
      value: null,
      label: 'Problem Trend by Date',
      trendData: dashboardData?.trend || [],
    });
  };

  const handleCloseTable = () => {
    setChartFilter({ visible: false, type: null, value: null, label: '' });
  };

  // Handle PDF export (placeholder for now)
  const handleExportPDF = async () => {
    if (!dashboardData) return;
    // TODO: Implement PDF export for problem dashboard
    console.log('PDF export for Problem Dashboard - Coming Soon');
  };

  const selectedCustomerName = customers.find(c => c.id === customerId)?.accountName || 'All Customers';

  return (
    <div className="space-y-6">
      {/* Filter Bar */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Customer Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
            <select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
            >
              <option value="">All Customers</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.accountName}
                </option>
              ))}
            </select>
          </div>

          {/* Duration Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
            <select
              value={durationPreset}
              onChange={(e) => handleDurationChange(e.target.value as DurationPreset)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
            >
              {durationPresets.map((preset) => (
                <option key={preset.value} value={preset.value}>
                  {preset.label}
                </option>
              ))}
            </select>
          </div>

          {/* Assignment Group Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assignment Group</label>
            <select
              value={assignmentGroupIds[0] || ''}
              onChange={(e) => setAssignmentGroupIds(e.target.value ? [e.target.value] : [])}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
            >
              <option value="">All Groups</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>

          {/* Export PDF Button and Refresh */}
          <div className="flex items-end gap-2">
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              title="Refresh dashboard data"
            >
              <ArrowPathIcon className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleExportPDF}
              disabled={!dashboardData}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
              Export PDF
            </button>
          </div>
        </div>

        {/* Last Updated Indicator */}
        {dataUpdatedAt && (
          <div className="mt-3 flex items-center justify-end text-xs text-gray-500">
            {isFetching && (
              <ArrowPathIcon className="w-3 h-3 mr-1 animate-spin" />
            )}
            <span>
              Last updated: {formatTime(new Date(dataUpdatedAt))}
            </span>
          </div>
        )}

        {/* Custom Date Range */}
        {durationPreset === 'custom' && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <DateTimePickerInput
                value={customDateFrom}
                onChange={(val) => setCustomDateFrom(val)}
                showTimeSelect={true}
                className="w-full"
              />
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <DateTimePickerInput
                  value={customDateTo}
                  onChange={(val) => setCustomDateTo(val)}
                  showTimeSelect={true}
                  className="w-full"
                />
              </div>
              <button
                onClick={handleApplyCustomDates}
                disabled={!customDateFrom || !customDateTo}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  customDatesNeedApply
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-primary-500 border-t-transparent"></div>
          <p className="mt-2 text-gray-500">Loading problem dashboard data...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Failed to load problem dashboard data. Please try again.
        </div>
      )}

      {/* Dashboard Content */}
      {dashboardData && !isLoading && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
            <div
              onClick={handleTotalProblemsClick}
              className="bg-white rounded-lg shadow p-4 text-center cursor-pointer hover:shadow-md hover:bg-gray-50 transition-all"
            >
              <div className="text-3xl font-bold text-gray-900">{dashboardData.summary.totalProblems}</div>
              <div className="text-sm text-gray-500">Total Problems</div>
            </div>
            <div
              onClick={handleOpenProblemsClick}
              className="bg-white rounded-lg shadow p-4 text-center cursor-pointer hover:shadow-md hover:bg-blue-50 transition-all"
            >
              <div className="text-3xl font-bold text-blue-600">{dashboardData.summary.openProblems}</div>
              <div className="text-sm text-gray-500">Open Problems</div>
            </div>
            <div
              onClick={handleResolvedClick}
              className="bg-white rounded-lg shadow p-4 text-center cursor-pointer hover:shadow-md hover:bg-green-50 transition-all"
            >
              <div className="text-3xl font-bold text-green-600">{dashboardData.summary.resolvedProblems}</div>
              <div className="text-sm text-gray-500">Resolved</div>
            </div>
            <div
              onClick={handleClosedClick}
              className="bg-white rounded-lg shadow p-4 text-center cursor-pointer hover:shadow-md hover:bg-gray-100 transition-all"
            >
              <div className="text-3xl font-bold text-gray-600">{dashboardData.summary.closedProblems}</div>
              <div className="text-sm text-gray-500">Closed</div>
            </div>
          </div>

          {/* Charts Row 1: State Distribution & Priority */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* State-wise Problem Count */}
            <ProblemStateBarChart
              data={dashboardData.byState}
              onBarClick={handleStateClick}
            />

            {/* Priority-wise Problem Count */}
            <PriorityDonutChart
              data={dashboardData.byPriority}
              onSegmentClick={handlePriorityClick}
            />
          </div>

          {/* Charts Row 2: Open Buckets & Tasks by Group */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Open Problem Buckets */}
            <ProblemBucketsChart
              data={dashboardData.openBuckets}
              onBucketClick={handleBucketClick}
            />

            {/* Open Problem Tasks by Assignment Group */}
            <ProblemTasksByGroupChart
              data={dashboardData.problemTasksByAssignmentGroup}
              onBarClick={handleGroupClick}
            />
          </div>

          {/* Charts Row 3: Trend Over Time (full width) */}
          <ProblemTrendChart
            data={dashboardData.trend}
            onPointClick={handleTrendPointClick}
          />

          {/* Filtered Tickets Table */}
          <UnifiedPulseReportTable
            visible={chartFilter.visible && chartFilter.type !== 'trend'}
            filterLabel={chartFilter.label}
            baseFilters={{
              customerId,
              timeRange,
              assignmentGroupId: assignmentGroupIds.join(','),
              timezoneOffset,
            }}
            chartFilters={{
              ticketType: 'PROBLEM',
              problemState: chartFilter.type === 'problemState' ? chartFilter.value || undefined : undefined,
              priority: chartFilter.type === 'priority' ? chartFilter.value || undefined : undefined,
              filterType: chartFilter.type === 'bucket' ? 'bucket' : chartFilter.type === 'assignmentGroup' ? 'assignmentGroup' : undefined,
              filterValue: (chartFilter.type === 'bucket' || chartFilter.type === 'assignmentGroup') ? chartFilter.value || undefined : undefined,
            }}
            onClose={handleCloseTable}
            ticketType="PROBLEM"
            customerName={selectedCustomerName}
          />
        </>
      )}
    </div>
  );
};

export default ProblemDashboard;
